// ============================================================
// passkey_registration_controller.js
// 패스키(WebAuthn) 등록·삭제 — /independence 보안 수단 섹션
// ============================================================
// 동작 방식:
//   register() → POST /passkeys/registration_options 로 create 옵션을 받고,
//                navigator.credentials.create 로 단말 인증기에 키를 만든 뒤,
//                POST /passkeys 로 공개키만 서버에 올린다. 성공하면 페이지를 새로고침해
//                서버가 렌더한 목록을 그대로 다시 받는다(목록 렌더 정본은 서버 한 곳).
//   destroy()  → DELETE /passkeys/:id 후 새로고침.
//
// 왜 패스키인가:
//   에이전트 채팅의 고위험 승인은 세션 쿠키만으로 눌리면 안 된다. 여기서 등록한 패스키가
//   그 승인 시점의 재인증 수단이 된다(미등록 사용자는 계정 PIN 폴백).
//
// 주의사항:
//   - 개인키는 단말을 벗어나지 않는다. 서버로 가는 것은 공개키와 서명뿐이다.
//   - 라벨은 전부 ERB(i18n)가 data-*-value 로 주입한다 — JS 에 문구를 하드코딩하지 않는다.
//   - base64url ↔ ArrayBuffer 변환은 자체 구현이다(npm 신규 의존 0 원칙).
//
// 사용 위치: independence/index.html.erb 의 패스키 섹션
// 값: optionsUrl(등록 옵션), createUrl(등록), labels(i18n 묶음)
// ============================================================

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  // status: 진행·실패 문구를 띄우는 자리.
  static targets = ["status", "nickname"]
  static values = {
    optionsUrl: String,
    createUrl: String,
    labels: Object
  }

  // ── 패스키 등록 ─────────────────────────────────────────────
  async register(e) {
    if (e) e.preventDefault()

    // 구형 브라우저·비 HTTPS 컨텍스트에서는 API 자체가 없다 — PIN 폴백으로 안내한다.
    if (!window.PublicKeyCredential) {
      this._setStatus(this.labelsValue.unsupported)
      return
    }

    this._setStatus(this.labelsValue.registering)

    let options
    try {
      const res = await this._post(this.optionsUrlValue)
      if (!res.ok) throw new Error("options_failed")
      options = (await res.json()).publicKey
    } catch (_) {
      this._setStatus(this.labelsValue.register_failed)
      return
    }

    let credential
    try {
      credential = await navigator.credentials.create({
        publicKey: this._decodeCreationOptions(options)
      })
      if (!credential) throw new Error("no_credential")
    } catch (_) {
      // 사용자가 프롬프트를 취소했거나 이미 등록된 인증기다.
      this._setStatus(this.labelsValue.register_failed)
      return
    }

    try {
      const nickname = this.hasNicknameTarget ? this.nicknameTarget.value.trim() : ""
      const res = await this._post(this.createUrlValue, {
        credential: JSON.stringify(this._encodeAttestation(credential)),
        nickname: nickname
      })
      if (!res.ok) throw new Error("create_failed")
    } catch (_) {
      this._setStatus(this.labelsValue.register_failed)
      return
    }

    this._setStatus(this.labelsValue.registered)
    window.location.reload()
  }

  // ── 패스키 삭제 ─────────────────────────────────────────────
  // 삭제 경로는 버튼이 data-passkey-registration-url-param 으로 실어 준다(서버 생성 경로만 사용).
  async destroy(e) {
    if (e) e.preventDefault()
    const url = (e && e.params && e.params.url) || ""
    if (!url) return
    if (!window.confirm(this.labelsValue.delete_confirm)) return

    try {
      const token = document.querySelector('meta[name="csrf-token"]')
      const res = await fetch(url, {
        method: "DELETE",
        headers: { "Accept": "application/json", "X-CSRF-Token": token ? token.content : "" }
      })
      if (!res.ok) throw new Error("delete_failed")
    } catch (_) {
      this._setStatus(this.labelsValue.delete_failed)
      return
    }

    window.location.reload()
  }

  // ── 내부 헬퍼 ───────────────────────────────────────────────

  _post(url, params = {}) {
    const token = document.querySelector('meta[name="csrf-token"]')
    return fetch(url, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": token ? token.content : ""
      },
      body: new URLSearchParams(params).toString()
    })
  }

  _setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text || ""
  }

  // base64url 은 '+/' 대신 '-_' 를 쓰고 패딩이 없다 — atob 에 넘기기 전에 되돌린다.
  _b64urlToBytes(value) {
    const base64 = String(value).replace(/-/g, "+").replace(/_/g, "/")
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4)
    const binary = atob(padded)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return bytes
  }

  _bytesToB64url(buffer) {
    const bytes = new Uint8Array(buffer)
    let binary = ""
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
  }

  // 서버가 준 create 옵션의 base64url 필드를 브라우저가 요구하는 버퍼로 바꾼다.
  // user.id 는 서버가 평문 문자열(user_uuid)로 주므로 문자 코드 그대로 바이트화한다.
  _decodeCreationOptions(publicKey) {
    const options = Object.assign({}, publicKey)
    options.challenge = this._b64urlToBytes(publicKey.challenge)
    options.user = Object.assign({}, publicKey.user, {
      id: Uint8Array.from(String(publicKey.user.id), (c) => c.charCodeAt(0))
    })
    options.excludeCredentials = (publicKey.excludeCredentials || []).map((c) => ({
      type: c.type,
      id: this._b64urlToBytes(c.id),
      transports: c.transports
    }))
    return options
  }

  // navigator.credentials.create 결과를 서버가 파싱할 수 있는 평범한 JSON 으로 바꾼다.
  _encodeAttestation(credential) {
    return {
      type: credential.type,
      id: credential.id,
      rawId: this._bytesToB64url(credential.rawId),
      clientExtensionResults: credential.getClientExtensionResults ? credential.getClientExtensionResults() : {},
      response: {
        clientDataJSON: this._bytesToB64url(credential.response.clientDataJSON),
        attestationObject: this._bytesToB64url(credential.response.attestationObject)
      }
    }
  }
}
